import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { ActivityLog } from '@/components/ActivityLog';
import { StatusBadge, TypeBadge } from '@/components/common/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Wrench, Plus, Search, MoreHorizontal, Pencil, Trash2, FileText, Play, CheckCircle, Package, ClipboardList, ChevronRight, ChevronLeft, Check, Users, Settings, ClipboardCheck, MapPin, Navigation, Loader2, Truck, ArrowUp, ArrowDown, BookOpen, PauseCircle, CircleDot, CalendarIcon, Clock, AlertTriangle, Shield, FileStack, List, LayoutGrid, FolderKanban } from 'lucide-react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { contactPrimaryLabel, isCommercialContact } from '@/lib/contactDisplay';
import { Pagination } from '@/components/common/Pagination';
import { useCounties } from '@/hooks/useCounties';
import PipelineBoard from '@/components/common/PipelineBoard';
import AttachCustomForm from '@/components/forms/AttachCustomForm';
import AttachedFormsBadge, { useAttachedFormCounts } from '@/components/forms/AttachedFormsBadge';
import { JOB_CATEGORY_VALUES, JOB_CATEGORY_LABELS, DEFAULT_JOB_CATEGORY, normalizeJobCategory } from '@/constants/jobCategories';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { useJobStatusConfig, jobStatusWorkType, jobStatusLabel } from '@/hooks/useJobStatusConfig';

// Wizard step definitions - now 5 steps including location
const WIZARD_STEPS = [
  { num: 1, key: 'customer', icon: Users },
  { num: 2, key: 'details', icon: Settings },
  { num: 3, key: 'location', icon: MapPin },
  { num: 4, key: 'items', icon: ClipboardCheck },
  { num: 5, key: 'team', icon: Wrench }
];

// Statuses now come from the configurable Job Status system (Settings > Job
// Status) — this just picks the right list per work type + prepends 'all'.
// When no specific type is selected, the union of every type's statuses is
// shown so the filter still works across mixed lists.
function getStatusesForType(config, typeFilter) {
  const allLabel = { key: 'all', label: 'all' };
  if (!typeFilter || typeFilter === 'all') {
    const seen = new Map();
    Object.values(config || {}).forEach(list => (list || []).forEach(s => {
      if (!seen.has(s.key)) seen.set(s.key, s);
    }));
    return [allLabel, ...Array.from(seen.values())];
  }
  const wt = jobStatusWorkType(typeFilter);
  return [allLabel, ...((config && config[wt]) || [])];
}

// Colors for the pipeline ("By Status") columns
const JOB_STATUS_COLORS = {
  unscheduled: { dotClass: 'bg-gray-400', headerClass: 'bg-gray-50 border-gray-300' },
  draft: { dotClass: 'bg-gray-400', headerClass: 'bg-gray-50 border-gray-300' },
  scheduled: { dotClass: 'bg-indigo-500', headerClass: 'bg-indigo-50 border-indigo-300' },
  in_route: { dotClass: 'bg-sky-500', headerClass: 'bg-sky-50 border-sky-300' },
  mast_up: { dotClass: 'bg-cyan-500', headerClass: 'bg-cyan-50 border-cyan-300' },
  mast_down: { dotClass: 'bg-teal-500', headerClass: 'bg-teal-50 border-teal-300' },
  well_log: { dotClass: 'bg-violet-500', headerClass: 'bg-violet-50 border-violet-300' },
  in_progress: { dotClass: 'bg-blue-500', headerClass: 'bg-blue-50 border-blue-300' },
  granted: { dotClass: 'bg-emerald-500', headerClass: 'bg-emerald-50 border-emerald-300' },
  sent_to_customer: { dotClass: 'bg-blue-500', headerClass: 'bg-blue-50 border-blue-300' },
  on_hold: { dotClass: 'bg-amber-500', headerClass: 'bg-amber-50 border-amber-300' },
  completed: { dotClass: 'bg-emerald-500', headerClass: 'bg-emerald-50 border-emerald-300' },
  cancelled: { dotClass: 'bg-gray-400', headerClass: 'bg-gray-100 border-gray-300' },
  in_dispute: { dotClass: 'bg-rose-500', headerClass: 'bg-rose-50 border-rose-300' },
};

// Well construction status progression
const WELL_CONSTRUCTION_FLOW = [
  { status: 'scheduled', label: 'Scheduled', icon: CircleDot },
  { status: 'in_route', label: 'In Route', icon: Truck },
  { status: 'mast_up', label: 'Mast Up', icon: ArrowUp },
  { status: 'mast_down', label: 'Mast Down', icon: ArrowDown },
  { status: 'well_log', label: 'Well Log', icon: BookOpen },
  { status: 'completed', label: 'Complete', icon: CheckCircle },
];

// General job status transitions
const GENERAL_TRANSITIONS = {
  unscheduled: [
    { status: 'scheduled', label: 'Schedule', icon: CircleDot },
  ],
  scheduled: [
    { status: 'in_progress', label: 'Start Work', icon: Play },
    { status: 'on_hold', label: 'Put on Hold', icon: PauseCircle },
  ],
  in_progress: [
    { status: 'completed', label: 'Mark Complete', icon: CheckCircle },
    { status: 'on_hold', label: 'Put on Hold', icon: PauseCircle },
  ],
  on_hold: [
    { status: 'in_progress', label: 'Resume Work', icon: Play },
  ],
};

// Get next status actions based on job type and current status
function getStatusActions(job) {
  if (normalizeJobCategory(job.type) === 'well_construction') {
    // Handle unscheduled → scheduled for well construction
    if (job.status === 'unscheduled') {
      return [{ status: 'scheduled', label: 'Schedule', icon: CircleDot }];
    }
    const currentIdx = WELL_CONSTRUCTION_FLOW.findIndex(s => s.status === job.status);
    if (currentIdx === -1 || currentIdx >= WELL_CONSTRUCTION_FLOW.length - 1) return [];
    // Next step in the progression
    const next = WELL_CONSTRUCTION_FLOW[currentIdx + 1];
    const actions = [next];
    // Also allow hold from any non-completed status
    if (job.status !== 'completed' && job.status !== 'on_hold') {
      actions.push({ status: 'on_hold', label: 'Put on Hold', icon: PauseCircle });
    }
    return actions;
  }
  // For on_hold well_construction, allow resuming to the status they were at
  if (job.status === 'on_hold') {
    return [{ status: 'in_progress', label: 'Resume Work', icon: Play }];
  }
  return GENERAL_TRANSITIONS[job.status] || [];
}
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
const TYPES = JOB_CATEGORY_VALUES;
const TYPE_LABELS = JOB_CATEGORY_LABELS;
// Mirrors backend/routes/jobs.py _ROLE_JOB_TYPE_RESTRICTION - used so the header/title
// always shows the role's actual restricted type, never a client-editable query param.
const ROLE_JOB_TYPE_RESTRICTION = { driller: 'well_construction', pump_installer: 'pump_installer' };

export default function JobsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, user } = useAuth();
  const counties = useCounties();
  const { config: jobStatusConfig } = useJobStatusConfig();
  const [searchParams] = useSearchParams();
  // Roles restricted to a single job type (mirrors backend's _ROLE_JOB_TYPE_RESTRICTION)
  // always see that type regardless of what ?type= is in the URL - the header/title
  // must reflect the actual data shown, not an editable query param.
  const roleRestrictedType = ROLE_JOB_TYPE_RESTRICTION[user?.role] || '';
  const typeFilter = roleRestrictedType || (searchParams.get('type') || '');
  const [jobs, setJobs] = useState([]);
  // Attached form counts per job (for the "X forms" badge in lists/cards)
  const formCounts = useAttachedFormCounts('job', jobs.map(j => j.id));
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [allEquipment, setAllEquipment] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || 'all');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'pipeline'
  const [pipelineJobs, setPipelineJobs] = useState([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 25;
  const [dialogOpen, setDialogOpen] = useState(false);

  // Reset status filter when type filter changes (avoid well_construction statuses on other types)
  useEffect(() => { setStatusFilter('all'); }, [typeFilter]);
  const [editing, setEditing] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [form, setForm] = useState({ 
    title: '', description: '', customer_id: '', priority: 'normal', type: DEFAULT_JOB_CATEGORY, notes: '', 
    items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0 }], 
    assigned_team: [{ user_id: '', role: 'driller' }], 
    equipment_used: [],
    source_estimate_id: '',
    // PLSS fields
    well_address: '', latitude: 0, longitude: 0, township: '', range_field: '', section: '', 
    parcel_id: '', property_owner: '', county: ''
  });
  
  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [slideDirection, setSlideDirection] = useState('right');
  const [lookupLoading, setLookupLoading] = useState(false);
  // Category suggestion (learn personas/tipos): pre-suggests the job type when a
  // person is assigned. Always editable; never forced.
  const [typeManuallySet, setTypeManuallySet] = useState(false);
  const [categorySuggestion, setCategorySuggestion] = useState(null); // { category, source, by }
  
  // Scheduling dialog state
  const [scheduleDialog, setScheduleDialog] = useState({ open: false, jobId: null });
  const [scheduleConflict, setScheduleConflict] = useState({ open: false, info: null });
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('08:00');

  // Dispute dialog state
  const [disputeDialog, setDisputeDialog] = useState({ open: false, jobId: null });
  const [disputeReason, setDisputeReason] = useState('');

  // Custom Forms state
  const [formsDialog, setFormsDialog] = useState({ open: false, job: null });

  // SKU/inventory search for items step (same UX as estimates wizard)
  const [lineItemResults, setLineItemResults] = useState([]);
  const [activeItemIdx, setActiveItemIdx] = useState(null);
  const searchTimeoutRef = useRef(null);

  const searchLineItems = (query, idx) => {
    setActiveItemIdx(idx);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!query || query.length < 1) {
      setLineItemResults([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/products/search', { params: { q: query } });
        setLineItemResults(res.data || []);
      } catch { setLineItemResults([]); }
    }, 150);
  };

  const selectLineItem = (lineItem, idx) => {
    const items = [...form.items];
    items[idx] = {
      ...items[idx],
      sku: lineItem.sku,
      name: lineItem.name,
      description: lineItem.description || lineItem.name,
      quantity: lineItem.default_quantity || items[idx].quantity || 1,
      unit: lineItem.unit || items[idx].unit || 'unit',
      unit_price: lineItem.unit_price || 0,  // ALWAYS selling price, never cost
    };
    setForm({ ...form, items });
    setLineItemResults([]);
    setActiveItemIdx(null);
    toast.success(t('estimates.itemAdded', { name: lineItem.name }));
  };

  const fetchData = useCallback(async () => {
    try {
      const params = { page, page_size: pageSize };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      const [jobsRes, contRes, usersRes, equipRes, estRes] = await Promise.all([
        api.get('/jobs', { params }),
        api.get('/contacts', { params: { page_size: 500 } }),
        api.get('/auth/users').catch(() => ({ data: [] })),
        api.get('/equipment').catch(() => ({ data: [] })),
        api.get('/estimates').catch(() => ({ data: [] }))
      ]);
      const jobData = jobsRes.data;
      setJobs(jobData.items || []);
      setTotal(jobData.total || 0);
      setTotalPages(jobData.total_pages || 1);
      const contData = contRes.data;
      setContacts(contData.items || contData);
      setUsers(usersRes.data);
      setAllEquipment(equipRes.data);
      const estData = estRes.data;
      setEstimates(Array.isArray(estData) ? estData : estData.items || []);
    } catch { toast.error(t('errors.loadingError')); }
    finally { setLoading(false); }
  }, [statusFilter, search, typeFilter, page, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Pipeline view loads ALL jobs (of the active type) regardless of the status tab.
  const fetchPipeline = useCallback(async () => {
    setPipelineLoading(true);
    try {
      const params = { page_size: 1000 };
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      const res = await api.get('/jobs', { params });
      setPipelineJobs(res.data.items || []);
    } catch { /* keep previous */ }
    finally { setPipelineLoading(false); }
  }, [search, typeFilter]);

  useEffect(() => {
    if (viewMode === 'pipeline') fetchPipeline();
  }, [viewMode, fetchPipeline]);

  // Auto-backfill job addresses on first load (one-time migration)
  useEffect(() => {
    const key = 'jobs_addr_backfill_done';
    if (!sessionStorage.getItem(key)) {
      api.post('/jobs/backfill-addresses').then(() => {
        sessionStorage.setItem(key, '1');
        fetchData(); // refresh list with backfilled addresses
      }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(1); }, [search, statusFilter, typeFilter]);

  useEffect(() => {
    if (location.state?.editJobId && jobs.length > 0) {
      const job = jobs.find(j => j.id === location.state.editJobId);
      if (job) openEdit(job);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, jobs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.state?.createFor && contacts.length > 0) {
      const customerId = location.state.createFor;
      const contactName = location.state.contactName || '';
      setEditing(null);
      setForm({ 
        title: contactName ? `${t('jobs.title')} - ${contactName}` : '', 
        description: '', 
        customer_id: customerId, 
        priority: 'normal', 
        type: DEFAULT_JOB_CATEGORY, 
        notes: '', 
        items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0 }], 
        assigned_team: [{ user_id: '', role: 'driller' }],
        equipment_used: []
      });
      setDialogOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, contacts, navigate, t]);

  // Handle createForProject from Project Detail navigation
  useEffect(() => {
    if (location.state?.createForProject) {
      const projectId = location.state.createForProject;
      const customerId = location.state.createFor || '';
      setEditing(null);
      setForm({ 
        title: '', description: '', customer_id: customerId, project_id: projectId,
        priority: 'normal', type: DEFAULT_JOB_CATEGORY, notes: '', 
        items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0 }], 
        assigned_team: [{ user_id: '', role: 'driller' }],
        equipment_used: []
      });
      setDialogOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Handle job creation from template
  const [fromTemplateId, setFromTemplateId] = useState(null);
  useEffect(() => {
    if (location.state?.fromTemplate) {
      const template = location.state.fromTemplate;
      setFromTemplateId(template.id);
      setEditing(null);
      
      // Map template items to job items format
      const mappedItems = template.default_items?.length > 0
        ? template.default_items.map(item => ({
            description: item.name || item.description || '',
            quantity: item.quantity || 1,
            unit: item.unit || 'unit',
            unit_price: item.unit_price || 0
          }))
        : [{ description: '', quantity: 1, unit: 'unit', unit_price: 0 }];
      
      setForm({
        title: template.name || '',
        description: template.description || '',
        customer_id: '',
        priority: 'normal',
        type: template.category || template.job_type || DEFAULT_JOB_CATEGORY,
        notes: template.notes || '',
        items: mappedItems,
        assigned_team: [{ user_id: '', role: 'driller' }],
        equipment_used: [],
        // Store template info for reference
        from_template: {
          id: template.id,
          name: template.name,
          checklist: template.checklist || []
        }
      });
      setWizardStep(1); // Start at step 1
      setDialogOpen(true);
      toast.info(t('jobTemplates.usingTemplate', { name: template.name }));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, t]);

  // Wizard navigation functions
  const goToNextStep = () => {
    if (wizardStep < 5) {
      setSlideDirection('right');
      setWizardStep(wizardStep + 1);
    }
  };

  const goToPrevStep = () => {
    if (wizardStep > 1) {
      setSlideDirection('left');
      setWizardStep(wizardStep - 1);
    }
  };

  // Auto-fill from estimate via data flow
  const handleEstimateSelect = async (estimateId) => {
    if (!estimateId) return;
    try {
      const res = await api.get(`/data-flow/from-estimate/${estimateId}`);
      const d = res.data;
      setForm(prev => ({
        ...prev,
        source_estimate_id: estimateId,
        customer_id: d.customer_id || prev.customer_id,
        project_id: d.project_id || prev.project_id,
        well_address: d.well_address || prev.well_address,
        latitude: d.latitude || prev.latitude,
        longitude: d.longitude || prev.longitude,
        county: d.county || prev.county,
        parcel_id: d.parcel_id || prev.parcel_id,
        township: d.township || prev.township,
        range_field: d.range || prev.range_field,
        section: d.section || prev.section,
        property_owner: d.customer_name || prev.property_owner,
        type: d.type || prev.type,
        well_details: d.well_info || prev.well_details,
        items: d.items?.length > 0 ? d.items : prev.items,
      }));
      toast.info('Datos del estimado heredados');
    } catch { /* ignore */ }
  };

  // Lookup property info from coordinates
  const lookupPropertyInfo = async () => {
    if (!form.latitude || !form.longitude) {
      toast.error(t('permits.enterCoordinates'));
      return;
    }
    
    setLookupLoading(true);
    try {
      const res = await api.post('/property/search_by_coords', {
        lat: form.latitude,
        lng: form.longitude,
        address: form.well_address || ''
      });
      
      if (res.data) {
        setForm(prev => ({
          ...prev,
          parcel_id: res.data.parcel_id || prev.parcel_id,
          property_owner: res.data.owner_name || prev.property_owner,
          well_address: res.data.address || prev.well_address,
          township: res.data.township || prev.township,
          range_field: res.data.range || prev.range_field,
          section: res.data.section || prev.section,
          county: res.data.county || prev.county
        }));
        toast.success(t('permits.propertyInfoFound'));
      }
    } catch (err) {
      console.error('Property lookup error:', err);
      toast.error(t('permits.propertyLookupFailed'));
    } finally {
      setLookupLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setIsViewMode(false);
    setFromTemplateId(null);
    setWizardStep(1);
    // If the user navigated via a typed filter, treat the type as intentionally
    // chosen so the assignment suggestion does not override it.
    setTypeManuallySet(!!typeFilter);
    setCategorySuggestion(null);
    setForm({ 
      title: '', description: '', customer_id: '', priority: 'normal', type: typeFilter || DEFAULT_JOB_CATEGORY, notes: '', 
      items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0 }], 
      assigned_team: [{ user_id: '', role: 'driller' }], 
      equipment_used: [],
      well_address: '', latitude: 0, longitude: 0, township: '', range_field: '', section: '', 
      parcel_id: '', property_owner: '', county: ''
    });
    setDialogOpen(true);
  };

  const openEdit = (job) => {
    setEditing(job);
    setIsViewMode(true);
    setFromTemplateId(null);
    setForm({
      title: job.title, description: job.description || '', customer_id: job.customer_id || '',
      priority: job.priority || 'normal', type: job.type || DEFAULT_JOB_CATEGORY, notes: job.notes || '',
      items: job.items?.length ? job.items : [{ description: '', quantity: 1, unit: 'unit', unit_price: 0 }],
      assigned_team: job.assigned_team?.length ? job.assigned_team : [{ user_id: '', role: 'driller' }],
      equipment_used: job.equipment_used || [],
      // PLSS fields
      well_address: job.well_address || '', 
      latitude: job.latitude || 0, 
      longitude: job.longitude || 0, 
      township: job.township || '', 
      range_field: job.range || '', 
      section: job.section || '', 
      parcel_id: job.parcel_id || '', 
      property_owner: job.property_owner || '', 
      county: job.county || ''
    });
    setDialogOpen(true);
  };

  const addTeamMember = () => setForm({ ...form, assigned_team: [...form.assigned_team, { user_id: '', role: 'helper' }] });
  const removeTeamMember = (idx) => setForm({ ...form, assigned_team: form.assigned_team.filter((_, i) => i !== idx) });
  const updateTeamMember = (idx, field, val) => {
    const team = [...form.assigned_team];
    team[idx] = { ...team[idx], [field]: val };
    setForm({ ...form, assigned_team: team });
    // When a person is assigned, suggest the job category from their default/history.
    if (field === 'user_id' && val && !editing) {
      fetchCategorySuggestion(val);
    }
  };

  const fetchCategorySuggestion = async (userId) => {
    try {
      const res = await api.get('/jobs/suggest-category', { params: { user_id: userId } });
      const cat = res.data?.category;
      if (!cat) { setCategorySuggestion(null); return; }
      const u = users.find(x => x.id === userId);
      const by = u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : '';
      setCategorySuggestion({ category: cat, source: res.data.source, by });
      // Pre-select only if the user hasn't manually chosen a category yet.
      setForm(prev => (!typeManuallySet && prev.type !== cat) ? { ...prev, type: cat } : prev);
    } catch (e) {
      setCategorySuggestion(null);
    }
  };

  const applySuggestedCategory = () => {
    if (categorySuggestion?.category) {
      setTypeManuallySet(false);
      setForm(prev => ({ ...prev, type: categorySuggestion.category }));
    }
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit: 'unit', unit_price: 0 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: field === 'quantity' || field === 'unit_price' ? parseFloat(val) || 0 : val };
    setForm({ ...form, items });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    // Guard: in the create wizard, never submit before the final step. This
    // prevents accidental job creation when pressing Enter inside an input.
    if (!editing && wizardStep < 5) { goToNextStep(); return; }
    if (!form.title.trim()) { toast.error(t('errors.requiredField')); return; }
    try {
      if (editing) {
        const res = await api.put(`/jobs/${editing.id}`, form);
        toast.success(t('success.updated'));
        // Alian (video): "Save and Close" makes the edit window disappear
        // and he loses his place — Save must stay on the same screen.
        // Refresh in place instead of closing; Cancel/X closes manually.
        setEditing(res.data);
        setIsViewMode(true);
        fetchData();
      } else {
        // Create the job
        const jobData = { ...form };
        delete jobData.from_template; // Remove template reference from job data
        await api.post('/jobs', jobData);

        // If created from a template, increment its usage count
        if (fromTemplateId) {
          try {
            await api.post(`/job-templates/${fromTemplateId}/use`);
          } catch (err) {
            console.error('Failed to increment template usage:', err);
          }
          setFromTemplateId(null);
        }

        toast.success(t('success.created'));
        setDialogOpen(false);
        fetchData();
      }
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    // Intercept scheduling to show date/time picker
    if (newStatus === 'scheduled') {
      setScheduleDate(new Date());
      setScheduleTime('08:00');
      setScheduleDialog({ open: true, jobId });
      return;
    }
    try {
      await api.put(`/jobs/${jobId}`, { status: newStatus });
      toast.success(t('success.updated'));
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else {
        toast.error(t('errors.savingError'));
      }
    }
  };

  const handleScheduleConfirm = async (override = false) => {
    if (!scheduleDate) {
      toast.error('Seleccione una fecha');
      return;
    }
    try {
      const d = scheduleDate;
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      await api.put(`/jobs/${scheduleDialog.jobId}`, {
        status: 'scheduled',
        scheduled_date: dateStr,
        scheduled_time: scheduleTime,
        ...(override ? { override_schedule_conflict: true } : {}),
      });
      toast.success(t('success.updated'));
      setScheduleDialog({ open: false, jobId: null });
      setScheduleConflict({ open: false, info: null });
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && detail.code === 'well_construction_day_conflict') {
        if (detail.requires_confirmation) {
          setScheduleConflict({ open: true, info: detail });
        } else {
          toast.error(detail.message || 'Solo se puede agendar un pozo por día. Elija otra fecha.');
        }
        return;
      }
      toast.error((detail && detail.message) || detail || t('errors.savingError'));
    }
  };

  const handleDisputeConfirm = async () => {
    if (!disputeReason.trim()) {
      toast.error(t('jobs.disputeReasonRequired'));
      return;
    }
    try {
      await api.post(`/jobs/${disputeDialog.jobId}/dispute`, { reason: disputeReason });
      toast.success(t('success.updated'));
      setDisputeDialog({ open: false, jobId: null });
      setDisputeReason('');
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || t('errors.savingError'));
    }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/jobs/${id}`); toast.success(t('success.deleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  const handleConvertToProject = async (job) => {
    try {
      const res = await api.post(`/jobs/${job.id}/convert-to-project`);
      const proj = res.data.project;
      if (res.data.created) toast.success(`Proyecto ${proj.project_number} creado`);
      else toast.info(res.data.message || 'El trabajo ya está vinculado a un proyecto');
      navigate(`/projects/${proj.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  const getCustomerName = (id) => { const c = contacts.find(c => c.id === id); return c ? contactPrimaryLabel(c) : '-'; };

  const getAssignedNames = (job) => {
    const team = job.assigned_team || [];
    if (team.length === 0) return '-';
    return team.map(m => {
      if (m.name) return m.name;
      const u = users.find(u => u.id === m.user_id);
      return u ? `${u.first_name} ${u.last_name}` : '';
    }).filter(Boolean).join(', ') || '-';
  };

  const getAssignedInitials = (job) => {
    const team = job.assigned_team || [];
    return team.map(m => {
      if (m.name) {
        const parts = m.name.trim().split(' ');
        return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
      }
      const u = users.find(u => u.id === m.user_id);
      return u ? `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}` : '';
    }).filter(Boolean);
  };

  return (
    <div data-testid="jobs-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <Wrench className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {typeFilter ? (TYPE_LABELS[typeFilter] || t('jobs.title')) : t('jobs.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('jobs.totalJobs', { count: jobs.length })}</p>
        </div>
        {hasPermission('jobs', 'create') && <Button data-testid="new-job-btn" onClick={openCreate} size="sm" className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm self-start sm:self-auto">
          <Plus className="w-4 h-4 mr-2" /> {t('jobs.newJob')}
        </Button>}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="search-jobs" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('common.search') + '...'} className="pl-10 h-9 bg-white" />
        </div>
        <div className="tabs-scroll">
          <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-auto">
            <TabsList className="bg-white border border-gray-200 h-9">
              {getStatusesForType(jobStatusConfig, typeFilter).map(s => (
                <TabsTrigger key={s.key} value={s.key} data-testid={`job-filter-${s.key}`} className="text-xs px-2 sm:px-2.5 data-[state=active]:bg-[#003366] data-[state=active]:text-white whitespace-nowrap">
                  {jobStatusLabel(t, s)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <div className="flex items-center rounded-lg border border-gray-200 bg-white h-9 overflow-hidden self-start">
          <button
            type="button" data-testid="jobs-view-list"
            onClick={() => setViewMode('list')}
            className={`px-3 h-full flex items-center gap-1.5 text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-[#003366] text-white' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <List className="w-3.5 h-3.5" /> <span className="hidden sm:inline">List</span>
          </button>
          <button
            type="button" data-testid="jobs-view-pipeline"
            onClick={() => setViewMode('pipeline')}
            className={`px-3 h-full flex items-center gap-1.5 text-xs font-medium transition-colors ${viewMode === 'pipeline' ? 'bg-[#003366] text-white' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <LayoutGrid className="w-3.5 h-3.5" /> <span className="hidden sm:inline">By Status</span>
          </button>
        </div>
      </div>

      {viewMode === 'pipeline' ? (
        pipelineLoading ? (
          <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
        ) : (
          <PipelineBoard
            columns={getStatusesForType(jobStatusConfig, typeFilter).filter(s => s.key !== 'all').map(s => ({
              key: s.key,
              label: jobStatusLabel(t, s),
              dotClass: (JOB_STATUS_COLORS[s.key] || {}).dotClass || 'bg-gray-400',
              headerClass: (JOB_STATUS_COLORS[s.key] || {}).headerClass || 'bg-gray-50 border-gray-300',
            }))}
            items={pipelineJobs}
            getStatus={(job) => job.status || 'unscheduled'}
            sumOf={(job) => job.total || 0}
            emptyLabel={t('jobs.noJobs')}
            renderCard={(job) => (
              <div
                key={job.id}
                data-testid={`pipeline-card-${job.id}`}
                onClick={() => navigate(`/jobs/${job.id}`)}
                className="bg-white rounded-lg border border-gray-100 p-3 shadow-sm hover:shadow-md cursor-pointer transition-shadow"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono font-semibold text-gray-700">{job.job_number}</span>
                  <span className="text-sm font-bold text-gray-900">${(job.total || 0).toLocaleString()}</span>
                </div>
                <p className="text-xs text-gray-600 truncate">{getCustomerName(job.customer_id)}</p>
                {job.title && <p className="text-[11px] text-gray-400 truncate">{job.title}</p>}
                {job.well_address && (
                  <p className="text-[11px] text-gray-400 truncate flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3 flex-shrink-0" /> {job.well_address}
                  </p>
                )}
                {(job.assigned_team || []).length > 0 && (
                  <p className="text-[11px] text-gray-500 truncate flex items-center gap-1 mt-1">
                    <Users className="w-3 h-3 flex-shrink-0" /> {getAssignedNames(job)}
                  </p>
                )}
              </div>
            )}
          />
        )
      ) : (
      <>
      {/* Desktop Table */}
      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : jobs.length === 0 ? (
            <div className="py-16 text-center">
              <Wrench className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('jobs.noJobs')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('jobs.createFirstJob')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('jobs.jobNumber')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.status')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('jobs.assignedTo')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('jobs.address')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('jobs.balance')}</th>
                    <th className="text-center text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">Files</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr key={job.id} data-testid={`job-row-${job.id}`} onClick={() => navigate(`/jobs/${job.id}`)} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer">
                      <td className="px-5 py-3.5">
                        <span className="text-sm font-mono font-medium text-gray-900">{job.job_number}</span>
                        <p className="text-xs text-gray-500 mt-0.5">{getCustomerName(job.customer_id)}</p>
                        {job.source_estimate_id && (
                          <span className="text-[10px] text-violet-500 mt-0.5 flex items-center gap-0.5">
                            <FileText className="w-2.5 h-2.5" /> {t('estimates.title')}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <StatusBadge status={job.status} />
                        {job.status === 'in_dispute' && job.dispute_reason && (
                          <p className="text-[10px] text-rose-500 mt-1 truncate max-w-[150px]" title={job.dispute_reason}>{job.dispute_reason}</p>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {(job.assigned_team || []).length > 0 ? (
                          <div className="flex items-center gap-1">
                            <div className="flex -space-x-1.5">
                              {getAssignedInitials(job).slice(0, 3).map((initials, i) => (
                                <div key={i} className="w-6 h-6 rounded-full bg-[#003366] text-white flex items-center justify-center text-[9px] font-bold ring-2 ring-white">
                                  {initials}
                                </div>
                              ))}
                            </div>
                            <span className="text-xs text-gray-600 ml-1 truncate max-w-[100px]">{getAssignedNames(job)}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {job.well_address ? (
                          <p className="text-sm text-gray-600 truncate max-w-[220px]" title={job.well_address} data-testid={`job-address-${job.id}`}>
                            <MapPin className="w-3 h-3 inline-block mr-1 text-gray-400 -mt-0.5" />
                            {job.well_address}
                          </p>
                        ) : (
                          <span className="text-xs text-gray-300 italic">Sin dirección</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right text-sm font-semibold text-gray-700">${(job.total || 0).toLocaleString()}</td>
                      <td className="px-5 py-3.5 text-center" data-testid={`job-files-count-${job.id}`}>
                        {(job.photos?.length || 0) > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-100">
                            {job.photos.length}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                        <AttachedFormsBadge
                          linkedType="job"
                          linkedId={job.id}
                          count={formCounts[job.id] ?? null}
                          onClick={() => setFormsDialog({ open: true, job })}
                          className="ml-1"
                        />
                      </td>
                      <td className="px-5 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`job-actions-${job.id}`} className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(job)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                            <DropdownMenuItem data-testid={`job-forms-${job.id}`} onClick={() => setFormsDialog({ open: true, job })}><FileStack className="w-3.5 h-3.5 mr-2 text-violet-500" />Forms</DropdownMenuItem>
                            {job.project_id ? (
                              <DropdownMenuItem data-testid={`job-view-project-${job.id}`} onClick={() => navigate(`/projects/${job.project_id}`)}><FolderKanban className="w-3.5 h-3.5 mr-2 text-emerald-600" />Ver Proyecto</DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem data-testid={`job-convert-project-${job.id}`} onClick={() => handleConvertToProject(job)}><FolderKanban className="w-3.5 h-3.5 mr-2 text-[#003366]" />Convertir a Proyecto</DropdownMenuItem>
                            )}
                            {getStatusActions(job).length > 0 && <DropdownMenuSeparator />}
                            {getStatusActions(job).map(action => (
                              <DropdownMenuItem key={action.status} onClick={() => handleStatusChange(job.id, action.status)}>
                                <action.icon className="w-3.5 h-3.5 mr-2" />{action.label}
                              </DropdownMenuItem>
                            ))}
                            {normalizeJobCategory(job.type) === 'well_construction' && job.status !== 'completed' && job.status !== 'cancelled' && job.status !== 'in_dispute' && (
                              <>
                                <DropdownMenuSeparator />
                                <div className="px-2 py-1.5">
                                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Well Progress</p>
                                  <div className="flex items-center gap-0.5 mt-1">
                                    {WELL_CONSTRUCTION_FLOW.map((step, i) => {
                                      const currentIdx = WELL_CONSTRUCTION_FLOW.findIndex(s => s.status === job.status);
                                      const isComplete = i <= currentIdx;
                                      const isCurrent = i === currentIdx;
                                      return (
                                        <div key={step.status} className={`h-1.5 flex-1 rounded-full ${isComplete ? 'bg-emerald-500' : isCurrent ? 'bg-blue-400' : 'bg-gray-200'}`} />
                                      );
                                    })}
                                  </div>
                                </div>
                              </>
                            )}
                            {job.status !== 'cancelled' && job.status !== 'in_dispute' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => { setDisputeDialog({ open: true, jobId: job.id }); setDisputeReason(''); }} className="text-rose-600" data-testid={`job-dispute-${job.id}`}>
                                  <AlertTriangle className="w-3.5 h-3.5 mr-2" />{t('jobs.markAsDispute')}
                                </DropdownMenuItem>
                              </>
                            )}
                            {hasPermission('jobs', 'delete') && <><DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(job.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem></>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-28 bg-white rounded-xl border border-gray-100 animate-pulse" />)}</div>
        ) : jobs.length === 0 ? (
          <Card className="bg-white border-gray-100"><CardContent className="py-12 text-center">
            <Wrench className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500 text-sm font-medium">{t('jobs.noJobs')}</p>
          </CardContent></Card>
        ) : (
          jobs.map((job, idx) => (
            <Card key={job.id} data-testid={`job-mobile-${job.id}`} onClick={() => navigate(`/jobs/${job.id}`)} className="bg-white border-gray-100 shadow-sm mobile-card-enter cursor-pointer hover:shadow-md transition-shadow" style={{ animationDelay: `${idx * 50}ms` }}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{getCustomerName(job.customer_id)}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-xs text-gray-400 font-mono">{job.job_number}</p>
                      {job.source_estimate_id && (
                        <span className="text-[10px] text-violet-500 flex items-center gap-0.5">
                          <FileText className="w-2.5 h-2.5" /> {t('estimates.title')}
                        </span>
                      )}
                    </div>
                    {job.well_address && (
                      <p className="text-xs text-gray-500 mt-1 truncate flex items-center gap-1" data-testid={`job-mobile-address-${job.id}`}>
                        <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        {job.well_address}
                      </p>
                    )}
                  </div>
                  <div onClick={e => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 -mt-1"><MoreHorizontal className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(job)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`job-forms-mobile-${job.id}`} onClick={() => setFormsDialog({ open: true, job })}><FileStack className="w-3.5 h-3.5 mr-2 text-violet-500" />Forms</DropdownMenuItem>
                        {job.project_id ? (
                          <DropdownMenuItem onClick={() => navigate(`/projects/${job.project_id}`)}><FolderKanban className="w-3.5 h-3.5 mr-2 text-emerald-600" />Ver Proyecto</DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem data-testid={`job-convert-project-mobile-${job.id}`} onClick={() => handleConvertToProject(job)}><FolderKanban className="w-3.5 h-3.5 mr-2 text-[#003366]" />Convertir a Proyecto</DropdownMenuItem>
                        )}
                        {getStatusActions(job).map(action => (
                          <DropdownMenuItem key={action.status} onClick={() => handleStatusChange(job.id, action.status)}>
                            <action.icon className="w-3.5 h-3.5 mr-2" />{action.label}
                          </DropdownMenuItem>
                        ))}
                        {job.status !== 'cancelled' && job.status !== 'in_dispute' && (
                          <DropdownMenuItem onClick={() => { setDisputeDialog({ open: true, jobId: job.id }); setDisputeReason(''); }} className="text-rose-600">
                            <AlertTriangle className="w-3.5 h-3.5 mr-2" />{t('jobs.markAsDispute')}
                          </DropdownMenuItem>
                        )}
                        {hasPermission('jobs', 'delete') && <DropdownMenuItem onClick={() => handleDelete(job.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={job.status} />
                    {job.status === 'in_dispute' && job.dispute_reason && (
                      <span className="text-[10px] text-rose-500 truncate max-w-[150px]" title={job.dispute_reason}>{job.dispute_reason}</span>
                    )}
                    {(job.assigned_team || []).length > 0 && (
                      <span className="text-[10px] text-gray-500 flex items-center gap-1">
                        <Users className="w-3 h-3" /> {getAssignedNames(job)}
                      </span>
                    )}
                    <AttachedFormsBadge
                      linkedType="job"
                      linkedId={job.id}
                      count={formCounts[job.id] ?? null}
                      onClick={() => setFormsDialog({ open: true, job })}
                    />
                  </div>
                  <span className="text-sm font-bold text-gray-800">${(job.total || 0).toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} />
      </>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => { 
        setDialogOpen(open); 
        if (!open) {
          setFromTemplateId(null);
          setWizardStep(1);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="font-['Barlow'] text-xl">{editing ? t('jobs.editJob') : t('jobs.newJob')}</DialogTitle>
              {editing && isViewMode && (
                <Button size="sm" onClick={() => setIsViewMode(false)} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="edit-mode-btn">
                  <Pencil className="w-4 h-4 mr-2" /> {t('common.edit')}
                </Button>
              )}
              {editing && isViewMode && editing.project_id && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/permits?project_id=${editing.project_id}`)}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="job-completion-report-btn"
                >
                  <FileText className="w-4 h-4 mr-2" /> View Permit
                </Button>
              )}
              {editing && isViewMode && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/permits?action=create&job_id=${editing.id}&project_id=${editing.project_id || ''}`)}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="create-permit-from-job-btn"
                >
                  <Shield className="w-4 h-4 mr-2" /> Crear Permiso
                </Button>
              )}
              {editing && !isViewMode && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded font-medium">Editando</span>
              )}
            </div>
          </DialogHeader>
          
          {/* Template Banner */}
          {fromTemplateId && form.from_template && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm flex-shrink-0">
              <ClipboardList className="w-4 h-4 text-blue-600" />
              <span className="text-blue-700">
                {t('jobs.creatingFromTemplate')}: <strong>{form.from_template.name}</strong>
              </span>
            </div>
          )}
          
          {/* Dispute Banner */}
          {editing && editing.status === 'in_dispute' && (
            <div className="flex items-center gap-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg text-sm flex-shrink-0" data-testid="dispute-banner">
              <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0" />
              <div>
                <span className="text-rose-700 font-medium">{t('statusBadges.in_dispute')}</span>
                {editing.dispute_reason && <p className="text-rose-600 text-xs mt-0.5">{editing.dispute_reason}</p>}
              </div>
            </div>
          )}
          
          {/* Wizard Step Indicator - Only for new jobs */}
          {!editing && (
            <div className="flex items-center justify-center py-4 border-b border-gray-100 flex-shrink-0">
              {WIZARD_STEPS.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <div key={step.num} className="flex items-center">
                    <button
                      type="button"
                      onClick={() => step.num <= wizardStep && setWizardStep(step.num)}
                      className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
                        wizardStep === step.num 
                          ? 'bg-[#003366] text-white shadow-lg scale-110' 
                          : wizardStep > step.num 
                            ? 'bg-emerald-500 text-white' 
                            : 'bg-gray-100 text-gray-400'
                      }`}
                      disabled={step.num > wizardStep}
                    >
                      {wizardStep > step.num ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </button>
                    {idx < WIZARD_STEPS.length - 1 && (
                      <ChevronRight className={`w-5 h-5 mx-2 transition-colors duration-300 ${wizardStep > step.num ? 'text-emerald-400' : 'text-gray-300'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          <form onSubmit={handleSave} className={`flex flex-col flex-1 overflow-hidden ${editing && isViewMode ? 'view-mode-form' : ''}`}>
            {/* Step Content with animation */}
            <div className="flex-1 overflow-y-auto py-4 px-1">
              <div 
                key={wizardStep}
                className="animate-in fade-in duration-300"
                style={{
                  animation: `${slideDirection === 'right' ? 'slideInRight' : 'slideInLeft'} 0.3s ease-out`
                }}
              >
                {/* Step 1: Customer Selection */}
                {(!editing && wizardStep === 1) && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-[#003366]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Users className="w-8 h-8 text-[#003366]" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{t('jobs.wizard.selectCustomer')}</h3>
                      <p className="text-sm text-gray-500 mt-1">{t('jobs.wizard.selectCustomerDesc')}</p>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-3">
                        {contacts.filter(c => c.is_customer).map(c => (
                          <div 
                            key={c.id}
                            onClick={() => setForm({...form, customer_id: c.id})}
                            className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                              form.customer_id === c.id 
                                ? 'border-[#003366] bg-[#003366]/5 shadow-md' 
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                form.customer_id === c.id ? 'bg-[#003366] text-white' : 'bg-gray-100 text-gray-500'
                              }`}>
                                {c.first_name?.[0]}{c.last_name?.[0]}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{contactPrimaryLabel(c)}</p>
                                {isCommercialContact(c) && `${c.first_name || ''} ${c.last_name || ''}`.trim() && <p className="text-sm text-gray-500">{`${c.first_name} ${c.last_name}`}</p>}
                              </div>
                              {form.customer_id === c.id && (
                                <CheckCircle className="w-5 h-5 text-[#003366]" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 2: Job Details */}
                {(!editing && wizardStep === 2) && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-[#003366]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Settings className="w-8 h-8 text-[#003366]" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{t('jobs.wizard.jobDetails')}</h3>
                      <p className="text-sm text-gray-500 mt-1">{t('jobs.wizard.jobDetailsDesc')}</p>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>{t('jobs.jobTitle')} *</Label>
                        <Input 
                          data-testid="job-title-input" 
                          value={form.title} 
                          onChange={e => setForm({...form, title: e.target.value})} 
                          placeholder={t('jobs.jobTitlePlaceholder')}
                          className="h-12 text-base"
                        />
                      </div>

                      {/* Source Estimate Selector - Data Flow */}
                      {estimates.length > 0 && (
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5 text-blue-500" />
                            {t('jobs.sourceEstimate', { defaultValue: 'Source Estimate (auto-fill)' })}
                          </Label>
                          <Select value={form.source_estimate_id || '_none'} onValueChange={v => { if (v !== '_none') handleEstimateSelect(v); }}>
                            <SelectTrigger className="h-10" data-testid="job-estimate-select">
                              <SelectValue placeholder={t('jobs.selectEstimate', { defaultValue: 'Select estimate to inherit data...' })} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">-- {t('common.none', { defaultValue: 'None' })} --</SelectItem>
                              {estimates.filter(e => e.status === 'accepted' || e.status === 'approved').map(e => (
                                <SelectItem key={e.id} value={e.id}>{e.estimate_number} - {e.title}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-gray-400">{t('jobs.estimateAutoFillDesc', { defaultValue: 'Selecting an estimate will auto-fill address, well info and items' })}</p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('common.type')}</Label>
                          <Select value={form.type} onValueChange={v => { setTypeManuallySet(true); setForm({...form, type: v}); }}>
                            <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {TYPES.map(tp => <SelectItem key={tp} value={tp}>{TYPE_LABELS[tp] || tp}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>{t('jobs.priority')}</Label>
                          <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                            <SelectTrigger className="h-12" data-testid="job-priority-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {PRIORITIES.map(p => <SelectItem key={p} value={p}>{t(`jobs.priorities.${p}`)}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t('common.description')}</Label>
                        <Textarea 
                          value={form.description} 
                          onChange={e => setForm({...form, description: e.target.value})} 
                          placeholder={t('common.description') + '...'} 
                          rows={3}
                          className="resize-none"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 3: Location & PLSS */}
                {(!editing && wizardStep === 3) && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-[#003366]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <MapPin className="w-8 h-8 text-[#003366]" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{t('jobs.wizard.wellLocation')}</h3>
                      <p className="text-sm text-gray-500 mt-1">{t('jobs.wizard.wellLocationDesc')}</p>
                    </div>

                    {/* 1) ADDRESS first — autocompleta el resto */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#003366]" />
                        {t('jobs.wizard.wellAddress')}
                      </Label>
                      <AddressAutocomplete
                        value={form.well_address || ''}
                        onChange={(addr) => {
                          const isTyping = addr.lat === null;
                          setForm({
                            ...form,
                            well_address: addr.address || '',
                            latitude: isTyping ? null : (addr.lat ? parseFloat(addr.lat) : form.latitude),
                            longitude: isTyping ? null : (addr.lng ? parseFloat(addr.lng) : form.longitude),
                            county: isTyping ? '' : (addr.county || form.county),
                          });
                        }}
                        placeholder={t('jobs.wizard.wellAddressPlaceholder')}
                      />
                      <p className="text-xs text-gray-400">
                        {t('jobs.wizard.wellAddressHint', { defaultValue: 'Start typing the address — coordinates and county autocomplete from the selection.' })}
                      </p>
                    </div>

                    {/* 2) GPS — pre-filled from address; user can override */}
                    <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                      <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                        <Navigation className="w-4 h-4" /> {t('permits.gpsCoordinates')}
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-xs">{t('permits.latitude')}</Label>
                          <Input 
                            type="number" 
                            step="0.000001"
                            value={form.latitude || ''} 
                            onChange={e => setForm({...form, latitude: parseFloat(e.target.value) || 0})} 
                            placeholder="27.4961"
                            className="bg-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">{t('permits.longitude')}</Label>
                          <Input 
                            type="number" 
                            step="0.000001"
                            value={form.longitude || ''} 
                            onChange={e => setForm({...form, longitude: parseFloat(e.target.value) || 0})} 
                            placeholder="-81.4601"
                            className="bg-white"
                          />
                        </div>
                      </div>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        className="mt-3 w-full"
                        onClick={lookupPropertyInfo}
                        disabled={lookupLoading || (!form.latitude && !form.longitude)}
                      >
                        {lookupLoading ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> {t('permits.searching')}</>
                        ) : (
                          <><Search className="w-3.5 h-3.5 mr-2" /> {t('permits.lookupProperty')}</>
                        )}
                      </Button>
                    </div>

                    {/* 3) PLSS / Parcel — derived/optional */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> {t('permits.plssInfo')}
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-2">
                          <Label className="text-xs">{t('permits.township')}</Label>
                          <Input 
                            value={form.township} 
                            onChange={e => setForm({...form, township: e.target.value})} 
                            placeholder="35S"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">{t('permits.range')}</Label>
                          <Input 
                            value={form.range_field} 
                            onChange={e => setForm({...form, range_field: e.target.value})} 
                            placeholder="29E"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">{t('permits.section')}</Label>
                          <Input 
                            value={form.section} 
                            onChange={e => setForm({...form, section: e.target.value})} 
                            placeholder="12"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-xs">{t('permits.parcelId')}</Label>
                          <Input 
                            value={form.parcel_id} 
                            onChange={e => setForm({...form, parcel_id: e.target.value})} 
                            placeholder="C-35-29-12-001-0010"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">{t('permits.county')}</Label>
                          <Select value={form.county} onValueChange={v => setForm({...form, county: v})}>
                            <SelectTrigger><SelectValue placeholder={t('permits.selectCounty')} /></SelectTrigger>
                            <SelectContent>
                              {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">{t('permits.propertyOwner')}</Label>
                        <Input 
                          value={form.property_owner} 
                          onChange={e => setForm({...form, property_owner: e.target.value})} 
                          placeholder={t('permits.propertyOwnerPlaceholder')}
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 4: Items/Services */}
                {(!editing && wizardStep === 4) && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-[#003366]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <ClipboardCheck className="w-8 h-8 text-[#003366]" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{t('jobs.wizard.itemsServices')}</h3>
                      <p className="text-sm text-gray-500 mt-1">{t('jobs.wizard.itemsServicesDesc')}</p>
                    </div>

                    {/* SKU search hint — same UX as estimates wizard */}
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50/50 border border-blue-100">
                      <Search className="w-4 h-4 text-blue-500" />
                      <p className="text-xs text-blue-600">{t('estimates.skuSearchHint')}</p>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold text-gray-700">{t('estimates.items')}</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addItem}>
                          <Plus className="w-3 h-3 mr-1" /> {t('estimates.addItem')}
                        </Button>
                      </div>
                      {form.items.map((item, idx) => (
                        <div key={idx} className="p-3 rounded-xl bg-gray-50 border border-gray-100 space-y-2">
                          {/* Top row: SKU search + Name + Delete */}
                          <div className="flex gap-2 items-start relative">
                            <div className="relative w-32">
                              <Input
                                data-testid={`job-item-sku-${idx}`}
                                value={item.sku || ''}
                                onChange={e => {
                                  updateItem(idx, 'sku', e.target.value);
                                  searchLineItems(e.target.value, idx);
                                }}
                                onFocus={() => {
                                  setActiveItemIdx(idx);
                                  if (item.sku) searchLineItems(item.sku, idx);
                                }}
                                placeholder={t('estimates.searchSku')}
                                className="bg-white h-9 font-mono text-sm"
                              />
                              {activeItemIdx === idx && lineItemResults.length > 0 && (
                                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 overflow-auto w-80">
                                  {lineItemResults.map((li) => (
                                    <button
                                      key={li.id}
                                      type="button"
                                      onClick={() => selectLineItem(li, idx)}
                                      className="w-full px-3 py-2.5 text-left hover:bg-blue-50 border-b last:border-0 transition-colors"
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="font-mono text-sm text-[#003366] font-bold">{li.sku}</span>
                                        <span className="text-xs font-semibold text-emerald-600">${(li.unit_price || 0).toFixed(2)}</span>
                                      </div>
                                      <p className="text-sm text-gray-800 truncate mt-0.5">{li.name}</p>
                                      {li.description && li.description !== li.name && (
                                        <p className="text-xs text-gray-400 truncate">{li.description}</p>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <Input
                              data-testid={`job-item-name-${idx}`}
                              value={item.name || ''}
                              onChange={e => {
                                updateItem(idx, 'name', e.target.value);
                                searchLineItems(e.target.value, idx);
                              }}
                              onFocus={() => {
                                setActiveItemIdx(idx);
                                if (item.name && !item.sku) searchLineItems(item.name, idx);
                              }}
                              placeholder={t('estimates.itemName')}
                              className="flex-1 bg-white h-9 font-medium"
                            />
                            {form.items.length > 1 && (
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} className="h-9 w-9 text-red-400 flex-shrink-0">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          {/* Bottom row: description + qty + price */}
                          <div className="flex gap-2">
                            <Input
                              value={item.description || ''}
                              onChange={e => updateItem(idx, 'description', e.target.value)}
                              placeholder={t('common.description')}
                              className="flex-1 bg-white h-9 text-sm"
                            />
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={e => updateItem(idx, 'quantity', e.target.value)}
                              className="w-20 bg-white h-9"
                              min="0"
                              placeholder={t('common.quantity')}
                            />
                            <Input
                              type="number"
                              value={item.unit_price}
                              onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                              className="w-28 bg-white h-9"
                              min="0"
                              step="0.01"
                              placeholder={t('common.price')}
                            />
                          </div>
                        </div>
                      ))}
                      
                      {/* Total */}
                      <div className="flex justify-end pt-2">
                        <div className="text-right">
                          <span className="text-sm text-gray-500">{t('common.total')}: </span>
                          <span className="text-xl font-bold text-gray-900">
                            ${form.items.reduce((sum, i) => sum + (i.quantity || 0) * (i.unit_price || 0), 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Step 5: Team & Equipment (or all fields when editing) */}
                {(editing || wizardStep === 5) && (
                  <div className="space-y-6">
                    {!editing && (
                      <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-[#003366]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Wrench className="w-8 h-8 text-[#003366]" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{t('jobs.wizard.teamEquipment')}</h3>
                        <p className="text-sm text-gray-500 mt-1">{t('jobs.wizard.teamEquipmentDesc')}</p>
                      </div>
                    )}
                    
                    {/* Show all fields when editing */}
                    {editing && (
                      <div className="space-y-4 mb-6">
                        <div className="space-y-2">
                          <Label>{t('jobs.jobTitle')} *</Label>
                          <Input data-testid="job-title-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder={t('jobs.jobTitlePlaceholder')} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>{t('common.type')}</Label>
                            <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {TYPES.map(tp => <SelectItem key={tp} value={tp}>{TYPE_LABELS[tp] || tp}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>{t('jobs.priority')}</Label>
                            <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                              <SelectTrigger data-testid="job-priority-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {PRIORITIES.map(p => <SelectItem key={p} value={p}>{t(`jobs.priorities.${p}`)}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>{t('common.customer')}</Label>
                          <Select value={form.customer_id} onValueChange={v => setForm({...form, customer_id: v})}>
                            <SelectTrigger><SelectValue placeholder={t('jobs.selectCustomer')} /></SelectTrigger>
                            <SelectContent>
                              {contacts.filter(c => c.is_customer).map(c => (
                                <SelectItem key={c.id} value={c.id}>{contactPrimaryLabel(c)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Items when editing */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold text-gray-700">{t('estimates.items')}</Label>
                            <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="w-3 h-3 mr-1" /> {t('estimates.addItem')}</Button>
                          </div>
                          {form.items.map((item, idx) => (
                            <div key={idx} className="flex gap-2 items-start p-3 rounded-lg bg-gray-50 border border-gray-100">
                              <Input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder={t('common.description')} className="flex-1 bg-white h-9" />
                              <Input type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="w-20 bg-white h-9" min="0" />
                              <Input type="number" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} className="w-28 bg-white h-9" min="0" step="0.01" />
                              {form.items.length > 1 && (
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} className="h-9 w-9 text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Team Assignment */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold text-gray-700">{t('jobs.assignedTo')}</Label>
                        <Button data-testid="wizard-add-team-btn" type="button" variant="outline" size="sm" onClick={addTeamMember}>
                          <Plus className="w-3 h-3 mr-1" /> {t('common.add')}
                        </Button>
                      </div>
                      {!editing && categorySuggestion?.category && (
                        <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm" data-testid="category-suggestion-banner">
                          <Wrench className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                          <span className="text-indigo-900">
                            {categorySuggestion.by ? `${categorySuggestion.by}: ` : ''}
                            categoría sugerida <strong>{TYPE_LABELS[categorySuggestion.category] || categorySuggestion.category}</strong>
                            {categorySuggestion.source === 'history' ? ' (según su historial)' : ''}
                          </span>
                          {form.type === categorySuggestion.category ? (
                            <Check className="w-4 h-4 text-emerald-500 ml-auto flex-shrink-0" />
                          ) : (
                            <Button type="button" variant="outline" size="sm" className="ml-auto h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-100" onClick={applySuggestedCategory} data-testid="apply-suggested-category-btn">
                              Aplicar
                            </Button>
                          )}
                        </div>
                      )}
                      {form.assigned_team.map((member, idx) => (
                        <div key={idx} className="flex gap-2 items-center p-3 rounded-xl bg-gray-50 border border-gray-100">
                          <Select value={member.user_id} onValueChange={v => updateTeamMember(idx, 'user_id', v)}>
                            <SelectTrigger className="flex-1 bg-white h-10" data-testid={`team-member-user-${idx}`}><SelectValue placeholder={t('jobs.selectDriller')} /></SelectTrigger>
                            <SelectContent>
                              {users.map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select value={member.role} onValueChange={v => updateTeamMember(idx, 'role', v)}>
                            <SelectTrigger className="w-32 bg-white h-10" data-testid={`team-role-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="driller">Driller</SelectItem>
                              <SelectItem value="operator">{t('settings.roles.office')}</SelectItem>
                              <SelectItem value="helper">Helper</SelectItem>
                              <SelectItem value="supervisor">Supervisor</SelectItem>
                            </SelectContent>
                          </Select>
                          {form.assigned_team.length > 1 && (
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeTeamMember(idx)} className="h-10 w-10 text-red-400">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Equipment Assignment */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                          <Package className="w-4 h-4 text-gray-400" />{t('jobs.equipmentUsed')}
                        </Label>
                        <Select onValueChange={v => {
                          const eq = allEquipment.find(e => e.id === v);
                          if (eq && !form.equipment_used.some(eu => eu.equipment_id === eq.id)) {
                            setForm({...form, equipment_used: [...form.equipment_used, {
                              equipment_id: eq.id,
                              equipment_name: eq.name,
                              equipment_type: eq.type,
                              serial_number: eq.serial_number || '',
                              assigned_date: new Date().toISOString().split('T')[0]
                            }]});
                          }
                        }}>
                          <SelectTrigger data-testid="job-equipment-select" className="w-auto h-8 text-xs bg-white border-dashed">
                            <span className="flex items-center gap-1"><Plus className="w-3 h-3" />{t('jobs.addEquipment')}</span>
                          </SelectTrigger>
                          <SelectContent>
                            {allEquipment.filter(eq => eq.status === 'active' && !form.equipment_used.some(eu => eu.equipment_id === eq.id)).map(eq => (
                              <SelectItem key={eq.id} value={eq.id}>{eq.name} {eq.serial_number ? `(${eq.serial_number})` : ''}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {form.equipment_used.length > 0 && (
                        <div className="space-y-1.5">
                          {form.equipment_used.map((eq, idx) => (
                            <div key={idx} data-testid={`form-equipment-${idx}`} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                              <div className="flex items-center gap-2">
                                <Package className="w-4 h-4 text-slate-500" />
                                <div>
                                  <p className="text-xs font-medium text-gray-800">{eq.equipment_name}</p>
                                  {eq.serial_number && <p className="text-[10px] text-gray-400 font-mono">{eq.serial_number}</p>}
                                </div>
                              </div>
                              <Button type="button" variant="ghost" size="icon" onClick={() => setForm({...form, equipment_used: form.equipment_used.filter((_, i) => i !== idx)})} className="h-7 w-7 text-red-400">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Notes */}
                    <div className="space-y-2">
                      <Label>{t('common.notes')}</Label>
                      <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder={t('common.notes') + '...'} rows={2} />
                    </div>

                    {/* Template Checklist Preview */}
                    {fromTemplateId && form.from_template?.checklist?.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <ClipboardList className="w-4 h-4 text-gray-500" />
                          <Label className="text-sm font-semibold text-gray-700">{t('jobTemplates.checklist')}</Label>
                          <span className="text-xs text-gray-400">({t('jobs.checklistFromTemplate')})</span>
                        </div>
                        <div className="space-y-1.5 bg-amber-50 border border-amber-100 rounded-lg p-3">
                          {form.from_template.checklist.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-3.5 h-3.5 text-amber-500" />
                              <span className="text-gray-700">{item.text}</span>
                              {item.required && (
                                <span className="text-[10px] font-medium text-red-500 px-1.5 py-0.5 bg-red-50 rounded">
                                  {t('common.required')}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Activity Log */}
            {editing && <ActivityLog entityType="jobs" entityId={editing?.id} />}

            {/* Footer with navigation */}
            {!(editing && isViewMode) && (
            <DialogFooter className="flex-shrink-0 border-t border-gray-100 pt-4 gap-2">
              {!editing && wizardStep > 1 && (
                <Button data-testid="wizard-back-btn" type="button" variant="outline" onClick={goToPrevStep} className="mr-auto">
                  <ChevronLeft className="w-4 h-4 mr-1" /> {t('common.back')}
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => { if (!isViewMode && editing) { setIsViewMode(true); } else { setDialogOpen(false); } }}>
                {!isViewMode && editing ? t('common.cancel') : t('common.close')}
              </Button>
              {!editing && wizardStep < 5 ? (
                <Button data-testid="wizard-next-btn" type="button" onClick={goToNextStep} className="bg-[#003366] hover:bg-[#004080] text-white">
                  {t('common.next')} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button data-testid="save-job-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                  {editing ? t('jobs.saveChanges') : t('jobs.createJob')}
                </Button>
              )}
            </DialogFooter>
            )}
          </form>
        </DialogContent>
      </Dialog>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* SCHEDULE DATE/TIME DIALOG */}
      <Dialog open={scheduleDialog.open} onOpenChange={(open) => { if (!open) setScheduleDialog({ open: false, jobId: null }); }}>
        <DialogContent className="sm:max-w-[420px]" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
                <CalendarIcon className="w-4 h-4 text-white" />
              </div>
              Programar Trabajo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Date Picker */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Fecha</Label>
              <div className="border rounded-lg overflow-hidden">
                <Calendar
                  mode="single"
                  selected={scheduleDate}
                  onSelect={setScheduleDate}
                  className="mx-auto"
                  data-testid="schedule-calendar"
                />
              </div>
            </div>
            {/* Time Picker */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-gray-400" />
                Hora
              </Label>
              <Input
                type="time"
                value={scheduleTime}
                onChange={e => setScheduleTime(e.target.value)}
                className="h-10"
                data-testid="schedule-time-input"
              />
            </div>
            {/* Summary */}
            {scheduleDate && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-center gap-3" data-testid="schedule-summary">
                <CalendarIcon className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-indigo-900">
                    {scheduleDate.toLocaleDateString('es-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                  <p className="text-xs text-indigo-600">
                    {scheduleTime ? `a las ${scheduleTime}` : 'Sin hora definida'}
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialog({ open: false, jobId: null })}>
              Cancelar
            </Button>
            <Button 
              onClick={() => handleScheduleConfirm(false)} 
              className="bg-[#003366] hover:bg-[#004080] text-white"
              data-testid="schedule-confirm-btn"
            >
              <CalendarIcon className="w-4 h-4 mr-1.5" />
              Programar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Well Construction capacity confirmation (admin only) */}
      <Dialog open={scheduleConflict.open} onOpenChange={(open) => { if (!open) setScheduleConflict({ open: false, info: null }); }}>
        <DialogContent className="sm:max-w-[440px]" data-testid="schedule-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
              </div>
              Ya hay un pozo agendado ese día
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2" data-testid="schedule-conflict-message">
            {scheduleConflict.info?.message || 'Ya existe un Well Construction agendado para ese día. ¿Desea agendar otro de todas formas?'}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleConflict({ open: false, info: null })} data-testid="schedule-conflict-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={() => handleScheduleConfirm(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="schedule-conflict-confirm-btn">
              Sí, agendar igual
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute Dialog */}
      <Dialog open={disputeDialog.open} onOpenChange={(open) => { if (!open) { setDisputeDialog({ open: false, jobId: null }); setDisputeReason(''); } }}>
        <DialogContent className="sm:max-w-[420px]" data-testid="dispute-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-white" />
              </div>
              {t('jobs.markAsDispute')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
              <p className="text-sm text-rose-700">{t('jobs.disputeWarning')}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">{t('jobs.disputeReason')} *</Label>
              <Textarea
                value={disputeReason}
                onChange={e => setDisputeReason(e.target.value)}
                placeholder={t('jobs.disputeReasonPlaceholder')}
                rows={3}
                className="resize-none"
                data-testid="dispute-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDisputeDialog({ open: false, jobId: null }); setDisputeReason(''); }}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleDisputeConfirm} 
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={!disputeReason.trim()}
              data-testid="dispute-confirm-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-1.5" />
              {t('jobs.confirmDispute')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Forms Dialog */}
      {formsDialog.open && formsDialog.job && (
        <AttachCustomForm
          open={formsDialog.open}
          onClose={() => setFormsDialog({ open: false, job: null })}
          linkedType="job"
          linkedId={formsDialog.job.id}
          context={{
            job_id: formsDialog.job.id,
            project_id: formsDialog.job.project_id || '',
            estimate_id: formsDialog.job.source_estimate_id || '',
            customer_id: formsDialog.job.customer_id || '',
          }}
          county={formsDialog.job.well_location?.county || formsDialog.job.county || null}
          title={`Forms — ${formsDialog.job.job_number || 'Job'}`}
        />
      )}
    </div>
  );
}
